import { LegacyRef, useEffect, useState } from 'react';
import { Icon, SelectGroupProps } from '@aqarmap/aqarmap-ui';
import SelectGroupController from '~/components/DesignSystem/Inputs/SelectGroupController';
import TextAreaInputController from '~/components/DesignSystem/Inputs/TextAreaInputController';
import { RateOptionEnum, RateReasonEnum } from '~/constants/rateEnums';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '~/redux/hooks';
import {
  useAddListingRateMutation,
  useGetRateValuesMutation,
  useGetUserQuery,
} from '~/redux/api/userApiSlice';
import RateFormLoader from '~/components/ListingRateForm/RateFormLoader';
import { useTranslation } from 'react-i18next';

type RateType = { label: string; id: number };

type FormDataType = {
  reasons: number[];
  comment: string;
  satisfactionType: number;
  user?: number;
  lead?: number;
};

type Props = {
  showRatingValues?: boolean;
  onSuccess?: () => void;
  onSubmit?: () => void;
  submitBtnRef: LegacyRef<HTMLButtonElement> | undefined;
};

const isCommentRequired = ({ reasons, comment }: FormDataType) =>
  reasons?.length && reasons.indexOf(RateReasonEnum.OTHERS) > -1 && !comment;

const ListingRateForm: React.FC<Props> = ({
  showRatingValues = false,
  onSuccess,
  onSubmit,
  submitBtnRef,
}) => {
  const { t } = useTranslation('app');
  const { control, handleSubmit, trigger, watch } = useForm<FormDataType>({});
  const [satisfactionType, setSatisfactionType] = useState<number | null>(null);
  const [satisfactionTypeError, setSatisfactionTypeError] =
    useState<string>('');
  const selectedReasons = watch('reasons') || [];
  const isOtherReason = selectedReasons.includes(RateReasonEnum.OTHERS);
  const [reasonsList, setReasonsList] = useState<SelectGroupProps['list']>([]);
  const [commentError, setCommentError] = useState<string>('');

  const ratingIcons: any = {
    [RateOptionEnum.SATISFIED]: 'satisfied',
    [RateOptionEnum.UNSATISFIED]: 'unsatisfied',
    [RateOptionEnum.NO_CONTACT]: 'no-contact',
  };
  const [
    getRateValuesMutation,
    { data: rateValues, isLoading: isRateValuesLoading },
  ] = useGetRateValuesMutation();
  const [addRateMutation, { isSuccess }] = useAddListingRateMutation();

  const ratedListing = useAppSelector((state) => state.listing.ratedListing);
  const { data: userData } = useGetUserQuery();
  const userId = userData?.user?.id;

  const onFormSubmit = async (data: FormDataType) => {
    if (!satisfactionType) {
      setSatisfactionTypeError(t('requiredSatisfactionType'));
      return;
    }

    if (satisfactionType === RateOptionEnum.UNSATISFIED) {
      const isReasonsValid = await trigger('reasons');
      if (!isReasonsValid) return;
    }

    if (isCommentRequired(data)) {
      setCommentError(t('requiredComment'));
      return;
    }

    if (onSubmit) onSubmit();
    await addRateMutation({
      listingId: ratedListing.id,
      ...data,
      satisfactionType,
      ...(userId && { user: userId }),
    });
  };

  useEffect(() => {
    if (isSuccess && onSuccess) onSuccess();
  }, [isSuccess]);

  useEffect(() => {
    if (rateValues?.reasons?.length) {
      const updatedReasons: SelectGroupProps['list'] = [];
      rateValues?.reasons.map((reason: any) => {
        updatedReasons.push({
          field: {
            value: reason.id,
            checked: false,
          },
          label: { text: reason.label },
        });
      });

      setReasonsList(updatedReasons);
    }
  }, [rateValues]);

  useEffect(() => {
    setSatisfactionTypeError('');
    watch(() => {
      setCommentError('');
    });
  }, [watch, satisfactionType]);

  useEffect(() => {
    setSatisfactionType(showRatingValues ? 0 : RateOptionEnum.UNSATISFIED);
    getRateValuesMutation({});
  }, []);

  if (isRateValuesLoading)
    return <RateFormLoader showRatingValues={showRatingValues} />;
  return (
    <form
      className="flex flex-col gap-3x w-full pb-3x h-auto"
      onSubmit={handleSubmit(onFormSubmit)}
    >
      <button ref={submitBtnRef} type="submit" hidden></button>
      {showRatingValues && (
        <div className="flex flex-col gap-2x w-full ">
          <p className="text-title_4 text-black">{t('listingRateFormTitle')}</p>
          <div className="flex gap-x items-center">
            {rateValues?.options?.map((option: RateType) => (
              <button
                key={option.id}
                type="button"
                className={`${
                  satisfactionType === option.id
                    ? 'text-blue'
                    : 'text-gray__dark_1'
                } flex flex-col gap-x w-full items-center`}
                onClick={() => {
                  setSatisfactionType(option.id);
                }}
              >
                <Icon name={ratingIcons[option.id]} size="48px" />
                <p className="text-body_2">{option.label}</p>
              </button>
            ))}
          </div>
          {satisfactionTypeError !== '' && (
            <p className="text-red_dark text-body_2">{satisfactionTypeError}</p>
          )}
        </div>
      )}
      {satisfactionType === RateOptionEnum.UNSATISFIED && (
        <div className="flex flex-col gap-3x w-full">
          <SelectGroupController
            list={reasonsList}
            control={control}
            ruleName="reasonsList"
            name="reasons"
            required={satisfactionType === RateOptionEnum.UNSATISFIED}
            size="medium"
            isMultiple={true}
            className="rating-form"
          />

          <TextAreaInputController
            field={{
              name: 'comment',
              placeholder: t('listingRateFormExplainIssue'),
              required: isOtherReason,
              className: 'resize-none',
            }}
            label={{
              text: t('listingRateFormExplainIssue'),
              className: 'text-title_4',
            }}
            ruleName="noteText"
            control={control}
            error={commentError}
          />
        </div>
      )}
    </form>
  );
};

export default ListingRateForm;
